/**
 * Admin · Communications Health Tile (Phase 2L-A88)
 *
 * Read-only operational pulse for the full customer comms pipeline:
 * outbound emails, inbound replies, referral invites, bounces,
 * send failures, and the unmatched-inbound triage queue depth.
 *
 * NEVER rendered for lab techs — admin-only.
 * NEVER mutates state — pure GET on a single read-only endpoint.
 * Tooltips on attention-marked metrics explain caveats inline.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity, Loader2, AlertTriangle, RefreshCw, ArrowRight,
  Send, Inbox, Ticket, MailX, MailWarning, AlertCircle,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const WINDOWS = [
  { id: "24h", label: "24 hours" },
  { id: "7d",  label: "7 days" },
  { id: "30d", label: "30 days" },
];

const METRIC_ICON = {
  outbound_sent:           Send,
  inbound_replies:         Inbox,
  referral_invites_sent:   Ticket,
  outbound_send_failures:  MailWarning,
  voucher_invite_bounces:  MailX,
  unmatched_parked:        AlertCircle,
  stale_send_failures:     MailWarning,
};

// Order matters — drives the grid layout. Informational first row,
// attention-prone second row.
const METRIC_ORDER = [
  "outbound_sent",
  "inbound_replies",
  "referral_invites_sent",
  "outbound_send_failures",
  "voucher_invite_bounces",
  "unmatched_parked",
  "stale_send_failures",
];

export function CommunicationsHealthTile() {
  const [window, setWindow] = useState("24h");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/admin/communications-health", {
        params: { window },
      });
      setData(res.data);
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, [window]);

  useEffect(() => { load(); }, [load]);

  const metrics = data?.metrics || {};

  return (
    <div
      data-testid="admin-communications-health-tile"
      className="rounded-3xl bg-white border border-slate-200 p-6"
    >
      <div className="flex items-start justify-between gap-3 mb-5">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-emerald-600" />
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700">
            Communications health
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="admin-comms-health-window"
            value={window}
            onChange={(e) => setWindow(e.target.value)}
            disabled={loading}
            className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs"
          >
            {WINDOWS.map((w) => (
              <option key={w.id} value={w.id}>{w.label}</option>
            ))}
          </select>
          <button
            type="button"
            data-testid="admin-comms-health-refresh"
            title="Refresh"
            onClick={load}
            disabled={loading}
            className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-700"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      {error ? (
        <div
          data-testid="admin-comms-health-error"
          className="flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>{error}</div>
        </div>
      ) : loading && !data ? (
        <div className="flex items-center gap-2 text-slate-400 text-sm py-6">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {METRIC_ORDER.map((id) => {
              const m = metrics[id];
              if (!m) return null;
              return <MetricCard key={id} id={id} metric={m} />;
            })}
          </div>

          <div className="mt-5 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
            <div>
              {data?.smoke_exclusion_note}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Link
                to="/admin/referral-invites"
                data-testid="admin-comms-health-link-invites"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-50 hover:bg-emerald-50 text-xs font-semibold text-slate-700 border border-slate-200"
              >
                Invite records <ArrowRight className="w-3 h-3" />
              </Link>
              <Link
                to="/admin/inbound-email/unmatched"
                data-testid="admin-comms-health-link-unmatched"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-50 hover:bg-emerald-50 text-xs font-semibold text-slate-700 border border-slate-200"
              >
                Unmatched inbound <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
}


function MetricCard({ id, metric }) {
  const Icon = METRIC_ICON[id] || Send;
  const attention = !!metric.attention;
  const tip = metric.tooltip || "";
  const ring = attention
    ? "border-rose-200 bg-rose-50/60"
    : "border-slate-200 bg-slate-50";
  const labelTone = attention ? "text-rose-700" : "text-slate-500";
  const countTone = attention ? "text-rose-900" : "text-slate-900";

  return (
    <div
      data-testid={`admin-comms-health-metric-${id}`}
      title={tip || undefined}
      className={`rounded-2xl border px-3 py-3 ${ring}`}
    >
      <div className={`flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold ${labelTone}`}>
        <Icon className="w-3 h-3" />
        <span className="truncate">{metric.label}</span>
        {attention && (
          <span
            data-testid={`admin-comms-health-metric-${id}-attention`}
            className="ml-auto inline-flex w-2 h-2 rounded-full bg-rose-500"
            aria-hidden="true"
          />
        )}
      </div>
      <div
        data-testid={`admin-comms-health-count-${id}`}
        className={`text-2xl font-display font-bold leading-tight mt-1 ${countTone}`}
      >
        {Number(metric.count || 0).toLocaleString()}
      </div>
    </div>
  );
}
